import { inject, Injectable, signal } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { SsApiService } from '../api/ss-api.service';
import { catchError, delay, filter, first, map, share, switchMap, tap } from 'rxjs/operators';
import { UserInfoService } from './user-info.service';
import { CmsContentMapperService } from '../cms-content-mapper.service';
import { CmsApiService } from '../api/cms-api.service';
import { CookieService } from 'ngx-unificator/services';
import { Router } from '@angular/router';
import { PlatformService } from '../platform.service';
import { EnvironmentService } from '../environment.service';
import { LoyaltyGroups } from '../loyalty/loyalty-data';
import { LocalstorageService } from '../localstorage.service';
import { BannerType } from '../../../../environments/environment';
import { BannersService } from '../banners.service';
import { GoogleTagManagerService } from '../google-tag-manager.service';
import { ModalService } from '../../modal-v2/modal.service';
import { CRYPTO_GROUP } from './data/user-group.data';
import { CurrencyService, NOT_NEEDED_CURRENCIES } from '../currency.service';
import { CountryService } from '../country.service';
import { useStore } from 'ngx-unificator/store';
import { UserStoreEntity } from '../../store/user.store';
import { GlobalStoreName } from '../../store';
import { UserType } from '../../types/user.type';

export enum UserSides {
  SAMURAI = 'samurai',
  NINJA = 'ninja',
  NOT_CHOOSEN = 'not_choosen',
}

export enum CURRENCY_EQUIVALENT{
  EUR = 1,
  USD = 1,
  NOK = 10,
  AUD = 1,
  CAD = 1,
  PHP = 60,
  BRL = 5,
  ZAR = 20,
  JPY = 160,
  PLN = 4,
  KRW = 1400,
}

export const STORAGE_LESS_MODAL_SHOWED = '--less-modal-was-shown';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private _ssApi = inject(SsApiService);
  private _cmsApi = inject(CmsApiService);
  private _currency = inject(CurrencyService);
  private _userInfo = inject(UserInfoService);
  private _contentMapper = inject(CmsContentMapperService);
  private _cookies = inject(CookieService);
  private _router = inject(Router);
  private _platform = inject(PlatformService);
  private _env = inject(EnvironmentService);
  private _storage = inject(LocalstorageService);
  private _banners = inject(BannersService);
  private _gtm = inject(GoogleTagManagerService);
  private _modal = inject(ModalService);
  private _country = inject(CountryService);

  private _userStore = useStore<UserStoreEntity>(GlobalStoreName.USER_STORE);

  public auth$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  public currency$: ReplaySubject<string> = new ReplaySubject<string>(1);

  /**
   * Player's additional data
   */
  private _playerStats: any = {};

  /**
   * User`s active currency (account)
   */
  private _currentCurrency = signal<any>({});

  private _currentCurrencyCompatibility = signal<any>({});

  /**
   * List of user accounts (currencies)
   */
  private _accountList = signal<any>([]);

  /**
   * Emits list of user accounts (currencies)
   */
  private _accountList$: ReplaySubject<any> = new ReplaySubject<any>(1);

  private _accountListCompatibility = signal([]);

  private _currentCurrencyCompatibility$: ReplaySubject<any> = new ReplaySubject<any>(1);

  public isUserAlreadyRegistered = signal<boolean>(this._cookies.check('registered')).asReadonly();

  private _loyaltyActiveLevel: string;

  private _isAlreadyChanged = false;

  private _authTranslateKey = signal<string>('btn.signup');

  constructor() {
    /**
     * For avoiding circular dependency
     */
    this._userInfo.userService = this;
    this._contentMapper.user = this;
  }

  get auth(): boolean { return this._userStore.storeSignal().auth; }
  public get info() { return this._userStore.storeSignal(); }
  get currentCurrency(): any { return this._currentCurrency(); }
  get currentCurrencyCompatibility(): any { return this._currentCurrencyCompatibility(); }
  get accountList(): Array<any> { return this._accountList(); }
  get accountList$(): ReplaySubject<any> { return this._accountList$; }
  get accountListCompatibility(): Array<any> { return this._accountListCompatibility(); }
  get stats(): any { return this._playerStats; }
  get hasDeposit(): boolean { return !!this._playerStats.deposits_count; }

  get currentCurrencyToEquivalent(): any {
    return CURRENCY_EQUIVALENT[this.currentCurrency?.currency];
  }


  get currentCurrencyCompatibility$(): ReplaySubject<any> { return this._currentCurrencyCompatibility$; }

  get loyaltyActiveLevel() { return this._loyaltyActiveLevel; }

  public patchUserStore(data: UserType) {
    this._userStore.storeSignal.update(e => {
      return {
        ...e,
        ...data,
      };
    });
  }



  /**
   * Get user account list and save to local var
   */
  getUserAccounts(): Observable<any> {
    return this._ssApi.playerAccounts().pipe(
      map(accountList => accountList.filter(account => !NOT_NEEDED_CURRENCIES.includes(account.currency))),
      map(accountList => this._serializeCurrencyValues(accountList)),
      catchError(() => of([])),
      tap(accountList => {
        this._accountList.set(accountList);
        this._accountList$.next(accountList);
        this.updateCurrentCurrencyBalance();
      })
    );
  }

  /**
   * Get user account compatibility list and save to local var
   */
  getUserAccountsCompatibility(): Observable<any> {
    return this._ssApi.playerAccounts({compatibility: false}).pipe(
      map(accountList => accountList.filter(account => !NOT_NEEDED_CURRENCIES.includes(account.currency))),
      map(accountList => this._serializeCurrencyValues(accountList)),
      map(accountList => accountList.filter(account => this.isHasCryptoAccounts() ?
        this._currency.isCryptoCurrency(account.currency) : accountList)),
      catchError(() => of([])),
      tap(accountList => {
        this._accountListCompatibility.set(accountList);
        this._currentCurrencyCompatibility.set(this._accountListCompatibility().find(account => account.currency === this.info.currency) || {});
        this._currentCurrencyCompatibility$.next(this._currentCurrencyCompatibility());
      })
    );
  }

  /**
   * Get user info and save to local var
   */
  getUserInfo(): Observable<any> {
    if (!this._platform.isBrowser) {
      this.patchUserStore({auth: false})
      this.auth$.next(false);
      return of({});
    }
    this.waitingForPlayerRequest();
    return this._ssApi.player().pipe(
      catchError(() => of({})),
      tap(info => {
        this.applyUserInfo(info);
      }),
    );
  }

  /**
   * Get additional player`s data from backend
   */
  getPlayerStats(): Observable<any> {
    return this._ssApi.playerStats().pipe(
      catchError(() => of({})),
      tap(stats => {
        this._playerStats = stats;
      })
    );
  }

  /**
   * Save user info to local var and declare user state
   *
   * @param info
   */
  applyUserInfo(info: any) {
    const userSide = info && info.statuses
      ? info.statuses.find((e) => e.id.toLowerCase() === UserSides.SAMURAI || e.id.toLowerCase() === UserSides.NINJA)
      : '';

    this.patchUserStore({
      ...info,
      gender_name: info.gender === 'm' ? 'Male' : info.gender === 'f' ? 'Female' : '',
      country_name: this._country.countryName(info.country || ''),
      userSide: userSide && userSide.id ? userSide.id.toLowerCase() : '',
      currency_symbol: this._currency.currencySymbol(info?.currency)
    });

    if (info.statuses && info.statuses.length) {
      const uGroup = {
        key: 'U-GROUP',
        val: info.statuses.map(status => status.id).join(',')
      }

      this._cmsApi.customHeadersList.push(uGroup);
      this._cmsApi.customNoLangHeadersList.push(uGroup);

      const loyaltyActiveLevel = Object.values(LoyaltyGroups).reverse().find(level => {
        return info.statuses.find(status => status.id === level);
      });

      if (loyaltyActiveLevel) {
        this._loyaltyActiveLevel = loyaltyActiveLevel;
      }
    }

    if ('id' in info) {
      this._cmsApi.customHeadersList.push({key: 'UID', val: `${info.id}`});
      this._cookies.set('UID', info.id);
      if (info.country) {
        this._cmsApi.customHeadersList.push({key: 'UC', val: info.country});
        this._cmsApi.customNoLangHeadersList.push({key: 'UC', val: info.country});
      }
      this.patchUserStore({auth: true});
      this.auth$.next(true);
      this._applyGoogleRegCurrency();
      this._setAlreadyRegisteredCookie();
      this._userInfo.checkMissingAuthFields(info);
    } else {
      this.patchUserStore({auth: false});
      this.auth$.next(false);
    }
  }

  private _serializeCurrencyValues(accountList: any[]) {
    return accountList.map(account => {
      return {
        ...account,
        amount: this._currency.subunitsToUnits(account.amount_cents, account.currency),
        cashout: this._currency.subunitsToUnits(account.available_to_cashout_cents, account.currency),
        symbol: this._currency.currencySymbol(account.currency),
        isCrypto: this._currency.cryptoCurrencyList().map(e => e.code).includes(account.currency),
      };
    });
  }

  private _setAlreadyRegisteredCookie() {
    this._cookies.set('registered', '1', 999, '/', (window.location.hostname as any));
  }

  /**
   * Fetch all required user data
   *
   * @private
   */
  public fetchAllUserData() {
    const observable = this.getUserInfo().pipe(
      first(),
      switchMap(() => this.getPlayerStats()),
      switchMap(() => this.getUserAccounts()),
      switchMap(() => this.getUserAccountsCompatibility()),
      share()
    );

    observable.subscribe();

    return observable;
  }

  /**
   * Update current currency balance info
   *
   * @public
   */
  public updateCurrentCurrencyBalance() {
    this._currentCurrency.set(this._accountList().find(account => account.currency === this.info.currency) || {});
    this.currency$.next(this._currentCurrency().currency);
  }

  /**
   * Change user currency account
   * @param selectedCurrency
   */
  public changeCurrencyAcc(selectedCurrency: string) {
    if (selectedCurrency) {
      this.currency$.next(null)
      this._ssApi.postPlayerAccounts({currency: selectedCurrency}).subscribe((e) => {
        this.fetchAllUserData();
      });
    }
  }

  public isHasCryptoAccounts() {
    if (this.info && this.info.statuses) {
      return this.info.statuses.some(status => status.id === CRYPTO_GROUP);
    }
  }

  /**
   * Waiting for /player response 2 seconds and emit default value
   * @private
   */
  private waitingForPlayerRequest() {
    if (this._platform.isBrowser) {
      setTimeout(() => {
        if (this.auth === undefined) {
          this.applyUserInfo({language: 'en'});
        }
      }, 2000);
    }
  }

  /**
   * Check if user allready registered and redirect
   */
  public authUser() {
    const isRegistered = this._cookies.check('registered');
    const urlReg = this._router.url + (isRegistered ? '?modal=login' : '?modal=registration');
    return this._router.navigateByUrl(urlReg);
  }

  /**
   * Check if user allready registered and return dymanic translate key
   */
  public get authKeyTranslate(): string {
    return this._authTranslateKey();
  }

  public setAuthTranslateKey(isSignUpRestrict: boolean = false) {
    this._authTranslateKey.set(isSignUpRestrict || this.isUserAlreadyRegistered() ? 'btn.login' : 'btn.signup');
  }

  /**
   * Update user currency when he register first time
   * @private
   */
  private _applyGoogleRegCurrency() {
    if (!this.isUserAlreadyRegistered() && !this._isAlreadyChanged) {
      this._isAlreadyChanged = true;
      this.changeCurrencyAcc(this._env.dataEnv()?.currency?.short);
    }
  }

  /**
   * Show less balance modal
   */
  public showLessBalanceModal() {
    return this._banners.item({
      type: BannerType.LOW_BALANCE,
      alias: 'low-balance'
    }).pipe(
      delay(2000),
      filter(data => !!data && !Boolean(this._storage.get(STORAGE_LESS_MODAL_SHOWED))),
      map(data => data?.[0]),
      filter(data => !! data),
      first(),
      tap( async (lowBalanceData) => {
        if (this.currentCurrency.amount < lowBalanceData.balance &&
          this.currentCurrency.amount > this.currentCurrencyToEquivalent) {
          this._gtm.lowBalancePopupStart('low_balance_popup', this.info.id);
          const component = await import('../../modal-v2/components/lazy/low-balance-modal/low-balance-modal.component');
          await this._modal.openLazy(component?.LowBalanceModalComponent, {
            data: lowBalanceData,
            template: 'CLEAR'
          });
          this._storage.set(STORAGE_LESS_MODAL_SHOWED, '1');
        }
      })
    ).subscribe();
  }
}
